import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import Button from '../ui/Button';
import {
  CLUB_POST_TYPE_OPTIONS,
  EVENT_POST_TYPE_OPTIONS,
  communityPostTypeLabel,
  type CommunityPost,
  type CommunityPostInput,
  type CommunityPostScope,
  type CommunityPostType,
} from '../../types/communityPosts';

function formatRelativeTime(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface ComposerProps {
  scope: CommunityPostScope;
  typeOptions: Array<{ value: CommunityPostType; label: string; hint: string }>;
  initial?: CommunityPost | null;
  busy?: boolean;
  onCancel?: () => void;
  onSubmit: (input: CommunityPostInput) => Promise<void>;
}

const PostComposer: React.FC<ComposerProps> = ({
  scope,
  typeOptions,
  initial = null,
  busy = false,
  onCancel,
  onSubmit,
}) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  const [postType, setPostType] = useState<CommunityPostType>(
    (initial?.post_type as CommunityPostType) || 'update'
  );
  const [isPinned, setIsPinned] = useState(Boolean(initial?.is_pinned));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(initial?.title || '');
    setBody(initial?.body || '');
    setPostType((initial?.post_type as CommunityPostType) || 'update');
    setIsPinned(Boolean(initial?.is_pinned));
    setError(null);
  }, [initial]);

  const charCount = body.length;
  const overLimit = charCount > 5000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) {
      setError('Write a short update before posting.');
      return;
    }
    if (overLimit) {
      setError('Updates can be up to 5,000 characters.');
      return;
    }
    setError(null);
    await onSubmit({
      title: title.trim() || undefined,
      body: trimmed,
      post_type: postType,
      is_pinned: isPinned,
    });
    if (!initial) {
      setTitle('');
      setBody('');
      setPostType('update');
      setIsPinned(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="form-section !rounded-2xl p-4 sm:p-5 space-y-3"
      aria-label={initial ? `Edit ${scope} update` : `Post ${scope} update`}
    >
      {!initial ? (
        <div>
          <p className="kicker mb-1">Share with the group</p>
          <p className="text-sm text-gray-400">
            {scope === 'club'
              ? 'Only active members can see this.'
              : 'Only registered entrants, judges, and you can see this.'}
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="input-label">Type</span>
          <select
            className="select-surface mt-1 w-full min-h-[44px]"
            value={postType}
            onChange={(e) => setPostType(e.target.value as CommunityPostType)}
            disabled={busy}
          >
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="input-helper">
            {typeOptions.find((o) => o.value === postType)?.hint}
          </span>
        </label>
        <label className="block">
          <span className="input-label">Headline (optional)</span>
          <input
            className="input mt-1 w-full min-h-[44px]"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Short subject"
            disabled={busy}
          />
        </label>
      </div>

      <label className="block">
        <span className="input-label">Update</span>
        <textarea
          className="input mt-1 w-full min-h-[120px] resize-y"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            scope === 'club'
              ? 'Meet at the usual lot Saturday at 9. Bring chairs.'
              : 'Gate opens at 7:30. Check-in is on the south side.'
          }
          disabled={busy}
          required
        />
        <span className={`input-helper ${overLimit ? 'text-red-400' : ''}`}>
          {charCount}/5000
        </span>
      </label>

      <label className="inline-flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
        <input
          type="checkbox"
          className="checkbox-control"
          checked={isPinned}
          onChange={(e) => setIsPinned(e.target.checked)}
          disabled={busy}
        />
        Pin to the top (max 3)
      </label>

      {error ? (
        <p className="input-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={busy || overLimit} className="min-h-[44px]">
          {busy ? 'Posting…' : initial ? 'Save changes' : 'Post update'}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} className="min-h-[44px]">
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
};

interface CommunityPostFeedProps {
  scope: CommunityPostScope;
  entityId: number;
  canView: boolean;
  canPost: boolean;
  initialItems?: CommunityPost[];
  initialTotal?: number;
  emptyMemberHint?: string;
  lockedHint?: string;
  className?: string;
}

export const CommunityPostFeed: React.FC<CommunityPostFeedProps> = ({
  scope,
  entityId,
  canView,
  canPost,
  initialItems = [],
  initialTotal = 0,
  emptyMemberHint,
  lockedHint,
  className = '',
}) => {
  const { showToast } = useToast();
  const [items, setItems] = useState<CommunityPost[]>(initialItems);
  const [total, setTotal] = useState(initialTotal);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState<CommunityPost | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(initialItems);
    setTotal(initialTotal);
    setPage(1);
  }, [initialItems, initialTotal, entityId]);

  const typeOptions = useMemo(
    () => (scope === 'club' ? CLUB_POST_TYPE_OPTIONS : EVENT_POST_TYPE_OPTIONS),
    [scope]
  );

  const refresh = useCallback(async () => {
    if (!canView || entityId <= 0) return;
    try {
      const data =
        scope === 'club'
          ? await apiService.getClubPosts(entityId, 1)
          : await apiService.getEventPosts(entityId, 1);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(1);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load updates.');
    }
  }, [canView, entityId, scope]);

  const loadMore = async () => {
    if (loadingMore || items.length >= total) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const data =
        scope === 'club'
          ? await apiService.getClubPosts(entityId, next)
          : await apiService.getEventPosts(entityId, next);
      setItems((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || total);
      setPage(next);
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not load more updates.', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCreate = async (input: CommunityPostInput) => {
    setPosting(true);
    try {
      const created =
        scope === 'club'
          ? await apiService.createClubPost(entityId, input)
          : await apiService.createEventPost(entityId, input);
      setItems((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      setTotal((t) => t + 1);
      showToast('Update posted', 'success');
      void apiService.trackEvent(scope === 'club' ? 'club_post_created' : 'event_post_created', {
        metadata: { entity_id: entityId, post_id: created.id },
      });
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not post update.', 'error');
      throw e;
    } finally {
      setPosting(false);
    }
  };

  const handleUpdate = async (input: CommunityPostInput) => {
    if (!editing) return;
    setPosting(true);
    try {
      const updated =
        scope === 'club'
          ? await apiService.updateClubPost(editing.id, input)
          : await apiService.updateEventPost(editing.id, input);
      setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setEditing(null);
      showToast('Update saved', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not save update.', 'error');
      throw e;
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (post: CommunityPost) => {
    if (!window.confirm('Remove this update? Members will no longer see it.')) return;
    try {
      if (scope === 'club') {
        await apiService.deleteClubPost(post.id);
      } else {
        await apiService.deleteEventPost(post.id);
      }
      setItems((prev) => prev.filter((p) => p.id !== post.id));
      setTotal((t) => Math.max(0, t - 1));
      if (editing?.id === post.id) setEditing(null);
      showToast('Update removed', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not remove update.', 'error');
    }
  };

  if (!canView) {
    return (
      <section id="updates" className={`scroll-mt-28 ${className}`.trim()}>
        <div className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.02] p-6 text-center">
          <p className="kicker mb-2">Updates</p>
          <h2 className="heading-section text-lg mb-2">Members only</h2>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            {lockedHint ||
              (scope === 'club'
                ? 'Join this club to read member updates.'
                : 'Register a vehicle for this event to read entrant updates.')}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="updates" className={`scroll-mt-28 space-y-4 ${className}`.trim()}>
      <div className="border-b border-white/[0.06] pb-4">
        <p className="kicker mb-2">{scope === 'club' ? 'Club board' : 'Entrant board'}</p>
        <h2 className="heading-section">Updates</h2>
        <p className="text-sm text-gray-500 mt-1.5">
          {scope === 'club'
            ? 'Private to active members.'
            : 'Private to registered entrants, judges, and the organizer.'}
        </p>
      </div>

      {canPost && !editing ? (
        <PostComposer
          scope={scope}
          typeOptions={typeOptions}
          busy={posting}
          onSubmit={handleCreate}
        />
      ) : null}

      {editing ? (
        <PostComposer
          scope={scope}
          typeOptions={typeOptions}
          initial={editing}
          busy={posting}
          onCancel={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      ) : null}

      {error ? (
        <div className="rounded-xl border border-white/[0.08] p-4 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-300" role="alert">
            {error}
          </p>
          <Button type="button" variant="secondary" size="sm" onClick={() => void refresh()}>
            Retry
          </Button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/50 p-8 text-center">
          <p className="text-white font-medium mb-1">No updates yet</p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            {canPost
              ? emptyMemberHint || 'Post the first note so everyone stays aligned.'
              : emptyMemberHint || 'When leads post, you’ll see it here.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-3 list-none">
          {items.map((post) => (
            <li
              key={post.id}
              className="rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/70 p-4 sm:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {post.is_pinned ? (
                      <span className="text-[11px] font-medium text-orange-300 border border-orange-500/30 bg-orange-500/[0.08] px-2 py-0.5 rounded-md">
                        Pinned
                      </span>
                    ) : null}
                    <span className="text-[11px] uppercase tracking-wide text-gray-500">
                      {communityPostTypeLabel(post.post_type)}
                    </span>
                  </div>
                  {post.title ? (
                    <h3 className="heading-card mt-1.5">{post.title}</h3>
                  ) : null}
                </div>
                <p className="text-xs text-gray-500 tabular-nums shrink-0">
                  {formatRelativeTime(post.published_at)}
                </p>
              </div>
              <p className="text-sm sm:text-base text-gray-200 whitespace-pre-wrap leading-relaxed">
                {post.body}
              </p>
              <div className="mt-3 pt-3 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                  {post.author?.display_name || post.author?.username || 'Member'}
                </p>
                {(post.can_edit || post.can_delete) && (
                  <div className="flex gap-2">
                    {post.can_edit ? (
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-white min-h-[36px] px-2"
                        onClick={() => setEditing(post)}
                      >
                        Edit
                      </button>
                    ) : null}
                    {post.can_delete ? (
                      <button
                        type="button"
                        className="text-xs text-red-300/80 hover:text-red-300 min-h-[36px] px-2"
                        onClick={() => void handleDelete(post)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {items.length < total ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary"
            disabled={loadingMore}
            onClick={() => void loadMore()}
            className="min-h-[44px]"
          >
            {loadingMore ? 'Loading…' : 'Load older updates'}
          </Button>
        </div>
      ) : null}
    </section>
  );
};

export default CommunityPostFeed;
